import React, { useRef, useState, useEffect } from "react";
import { TableRowData } from "./data-table";
import { Button } from "./ui/button";
import { Icons } from "./ui/icons";
import { X, Settings2 } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { getQuotation, Quotation, QuotationItem } from "../lib/api";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import {
  evaluateFormula,
  getLineTotalFormulaColumn,
} from "../lib/quote-invoice-columns";
import { getBackendErrorMessage } from "../lib/utils";

interface QuoteDrawerProps {
  selectedQuoteDetails: TableRowData | null;
  quoteDetails: TableRowData | null;
  onClose: () => void;
}

interface ColumnVisibility {
  description: boolean;
  unit: boolean;
  quantity: boolean;
  unitPrice: boolean;
  total: boolean;
}

const LEGACY_COLUMN_OPTIONS: { key: keyof ColumnVisibility; label: string }[] =
  [
    { key: "description", label: "Description" },
    { key: "unit", label: "Unit" },
    { key: "quantity", label: "Quantity" },
    { key: "unitPrice", label: "Unit Price" },
    { key: "total", label: "Total" },
  ];

const QuoteDrawer: React.FC<QuoteDrawerProps> = ({
  selectedQuoteDetails,
  quoteDetails,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const printRef = useRef<HTMLDivElement>(null);
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(false);
  const [legacyVisibility, setLegacyVisibility] = useState<ColumnVisibility>({
    description: true,
    unit: true,
    quantity: true,
    unitPrice: true,
    total: true,
  });

  const useDynamicColumns = Boolean(quotation?.columnConfig?.columns?.length);
  const columns = quotation?.columnConfig?.columns ?? [];
  const columnVisibility: Record<string, boolean> = useDynamicColumns
    ? (quotation!.columnConfig!.columnVisibility ??
      columns.reduce<Record<string, boolean>>(
        (acc, col) => ({ ...acc, [col.id]: true }),
        {},
      ))
    : {
        description: legacyVisibility.description,
        unit: legacyVisibility.unit,
        quantity: legacyVisibility.quantity,
        unitPrice: legacyVisibility.unitPrice,
        total: legacyVisibility.total,
      };

  const getRowForFormula = (item: QuotationItem, index: number) => {
    const qty = Number(item.quantity ?? 0) || 0;
    const unitPrice = Number(parseFloat(item.unitPrice as string) || 0) || 0;
    const lineTotal = qty * unitPrice;
    const data = item.data as Record<string, string | number> | undefined;
    const discountType = String(
      (data?.discountType as string) ?? "None",
    ).trim();
    const discountVal = Number(data?.discount) || 0;
    const discountAmount =
      discountType === "Fixed"
        ? discountVal
        : discountType === "Percentage"
          ? Math.round(((lineTotal * discountVal) / 100) * 100) / 100
          : 0;
    return {
      ...(item.data ?? {}),
      sno: index + 1,
      description: item.description ?? "",
      unit: item.unit ?? "",
      qty,
      unitPrice,
      discountAmount,
    };
  };

  const getRowAmount = (item: QuotationItem, index: number): number => {
    if (useDynamicColumns && columns.length) {
      const formulaCol = getLineTotalFormulaColumn(columns);
      if (formulaCol?.formula) {
        return evaluateFormula(
          formulaCol.formula,
          getRowForFormula(item, index),
          columns,
        );
      }
    }
    return item.quantity * (parseFloat(item.unitPrice) || 0);
  };

  const getCellValue = (
    item: QuotationItem,
    colId: string,
  ): string | number => {
    if (colId === "sno") return 0;
    if (colId === "description") return item.description ?? "";
    if (colId === "unit") return item.unit ?? "";
    if (colId === "qty") return item.quantity ?? 0;
    if (colId === "unitPrice") return parseFloat(item.unitPrice) || 0;
    if (colId === "discountType")
      return (
        (item.data as Record<string, string | number> | undefined)
          ?.discountType ?? "None"
      );
    if (colId === "discount")
      return (
        (item.data as Record<string, string | number> | undefined)?.discount ??
        0
      );
    const v = (item.data as Record<string, string | number> | undefined)?.[
      colId
    ];
    return v !== undefined && v !== "" ? v : "";
  };

  const visibleColumns = useDynamicColumns
    ? columns.filter((c) => columnVisibility[c.id] !== false)
    : null;

  useEffect(() => {
    const fetchQuotation = async () => {
      if (selectedQuoteDetails?.id) {
        setLoading(true);
        try {
          const data = await getQuotation(selectedQuoteDetails.id as string);
          setQuotation(data);
        } catch (error) {
          toast.error(
            getBackendErrorMessage(error, "Failed to fetch quotation"),
          );
        } finally {
          setLoading(false);
        }
      }
    };

    fetchQuotation();
  }, [selectedQuoteDetails?.id]);

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title =
      quotation?.reference && quotation?.customer?.fullName
        ? `${quotation.title ?? "Quote"} - ${quotation.reference} - ${quotation.customer.fullName}`
        : "Quote";
    window.print();

    document.title = originalTitle;
    if (parent) parent.insertBefore(el, next);
    else el.remove();
  };

  const quotationItems = quotation?.items ?? [];
  const subtotal = quotationItems.reduce(
    (total, item, i) => total + getRowAmount(item, i),
    0,
  );
  const vat = parseFloat(quotation?.vat || "0");
  const total = parseFloat(quotation?.totalAmount || "0");

  const formulaCols =
    visibleColumns?.filter((c) => c.type === "formula" && c.formula) ?? [];
  const formulaSums =
    formulaCols.length > 0
      ? formulaCols.map(
          (col) =>
            Math.round(
              quotationItems.reduce(
                (sum, item, i) =>
                  sum +
                  evaluateFormula(
                    col.formula!,
                    getRowForFormula(item, i),
                    columns,
                  ),
                0,
              ) * 100,
            ) / 100,
        )
      : [];

  const lineTotalFormulaId = getLineTotalFormulaColumn(columns)?.id;

  return (
    <SidePanel
      open={!!quoteDetails}
      onClose={onClose}
      width="w-[1000px]"
      layer="overlay"
    >
      {quoteDetails && (
        <div className="relative h-full flex flex-col">
          {isMobile ? (
            <PanelHeader
              title={`Quote ${quotation?.reference ?? ""}`.trim()}
              onClose={onClose}
              actions={[
                {
                  label: "Print",
                  icon: Icons.Print,
                  onClick: handlePrint,
                  disabled: !quotation,
                  pinned: true,
                },
                ...(!useDynamicColumns
                  ? LEGACY_COLUMN_OPTIONS.map(({ key, label }) => ({
                      label: `${legacyVisibility[key] ? "Hide" : "Show"} ${label} column`,
                      icon: Settings2,
                      onClick: () =>
                        setLegacyVisibility((prev) => ({
                          ...prev,
                          [key]: !prev[key],
                        })),
                    }))
                  : []),
              ]}
            />
          ) : (
            <div className="px-4 md:px-6 py-4 flex-shrink-0 border-b border-gray-100">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                <h2 className="text-base md:text-lg font-semibold text-foreground font-jakarta md:mr-auto">
                  Quote {quotation?.reference}
                </h2>
                <div className="flex items-center gap-2 w-full md:w-auto">
                  {!useDynamicColumns && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex-1 md:flex-initial"
                        >
                          <Settings2 className="mr-2 h-4 w-4" />
                          Columns
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {LEGACY_COLUMN_OPTIONS.map(({ key, label }) => (
                          <DropdownMenuCheckboxItem
                            key={key}
                            checked={columnVisibility[key]}
                            onCheckedChange={(checked) =>
                              setLegacyVisibility((prev) => ({
                                ...prev,
                                [key]: !!checked,
                              }))
                            }
                          >
                            {label}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button variant="outline" onClick={handlePrint}>
                    <Icons.Print className="mr-2" />
                    Print
                  </Button>
                  <Button
                    variant="outline"
                    className="h-8 w-8 p-0 flex-shrink-0"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="flex-grow overflow-hidden">
            <PanelScroll isMobile={isMobile}>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Loading quotation...</p>
                </div>
              ) : quotation ? (
                <div className="w-full p-4 md:p-6 bg-gray-50">
                  <ScaledPreview>
                    <div
                      ref={printRef}
                      className="print-area p-3 md:p-10 bg-white shadow-lg rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-6 md:mb-8">
                        <h1 className="text-3xl font-bold">
                          {quotation.title ?? "Quote"}
                        </h1>
                        <img
                          src="/images/logo.png"
                          alt="Company Logo"
                          className="w-80"
                        />
                      </div>

                      <section className="flex flex-row gap-4 text-sm">
                        <div className="flex-1">
                          <h1 className="text-sm md:text-base font-semibold">
                            {quotation.customer.fullName}
                          </h1>
                          <p>
                            {quotation.customer.postalCode
                              ? `P.O.BOX ${quotation.customer.postalCode}, `
                              : ""}
                            {quotation.customer.street
                              ? `${quotation.customer.street},`
                              : ""}
                          </p>
                          <p>
                            {quotation.customer.ward
                              ? `${quotation.customer.ward}, `
                              : ""}
                            {quotation.customer.district
                              ? `${quotation.customer.district}.`
                              : ""}
                          </p>
                          <p>
                            {quotation.customer.region
                              ? `${quotation.customer.region}, `
                              : ""}
                            {quotation.customer.country
                              ? `${quotation.customer.country}.`
                              : ""}
                          </p>
                          {quotation.customer.phoneNumber && (
                            <p>Phone: {quotation.customer.phoneNumber}</p>
                          )}
                          <p>
                            {quotation.customer.vrn
                              ? `VRN: ${quotation.customer.vrn}`
                              : ""}
                          </p>
                          <p>
                            {quotation.customer.tin
                              ? `TIN: ${quotation.customer.tin}`
                              : ""}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm">
                            <strong>Issue Date</strong>
                            <br />{" "}
                            {format(
                              new Date(quotation.issueDate),
                              "dd-MM-yyyy",
                            )}
                          </p>
                          <br />
                          {quotation.expiryDate && (
                            <p className="text-sm">
                              <strong>Expiry Date</strong>
                              <br />{" "}
                              {format(
                                new Date(quotation.expiryDate),
                                "dd-MM-yyyy",
                              )}
                            </p>
                          )}
                          <br />
                          <p className="text-sm">
                            <strong>Reference</strong>
                            <br /> {quotation.reference}
                          </p>
                        </div>
                        <div className="block border-l border-gray-800" />
                        <div className="pt-4 md:pt-0">
                          <h1 className="text-sm md:text-base font-semibold">
                            WAPO SCAFFOLDING (T) LIMITED
                          </h1>
                          <p>
                            Wazo Hill Tegeta,
                            <br />
                            Plot No. 161 Block C,
                          </p>
                          <p>
                            P.O Box 35854,
                            <br />
                            Dar es Salaam, Tanzania
                          </p>
                          <br />
                          <p>TIN: 124-154-243</p>
                          <p>VRN: 40-028187-U</p>
                          <br />
                          <p>Email: info@waposcaffolding.com</p>
                          <p>Website: www.waposcaffolding.co.tz</p>
                          <br />
                          <p>Phone: +255 (0) 716 029 263</p>
                        </div>
                      </section>

                      <h2 className="mt-6 md:mt-10 mb-4 text-base md:text-lg font-semibold">
                        {quotation.description ??
                          "We are pleased to quote you for the following"}
                      </h2>

                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs md:text-sm">
                          <thead>
                            <tr>
                              {visibleColumns ? (
                                visibleColumns.map((col) => (
                                  <th
                                    key={col.id}
                                    className={
                                      col.id === "description"
                                        ? "border border-gray-300 px-2 md:px-4 py-2 text-start"
                                        : col.type === "formula"
                                          ? "border border-gray-300 px-2 md:px-4 py-2 text-end tabular-nums min-w-[7rem]"
                                          : "border border-gray-300 px-2 md:px-4 py-2"
                                    }
                                  >
                                    {col.label}
                                  </th>
                                ))
                              ) : (
                                <>
                                  <th className="border border-gray-300 px-2 md:px-4 py-2">
                                    #
                                  </th>
                                  {columnVisibility.description && (
                                    <th className="border border-gray-300 px-2 md:px-4 py-2 text-start">
                                      Description
                                    </th>
                                  )}
                                  {columnVisibility.unit && (
                                    <th className="border border-gray-300 px-2 md:px-4 py-2">
                                      Unit
                                    </th>
                                  )}
                                  {columnVisibility.quantity && (
                                    <th className="border border-gray-300 px-2 md:px-4 py-2">
                                      Qty
                                    </th>
                                  )}
                                  {columnVisibility.unitPrice && (
                                    <th className="border border-gray-300 px-2 md:px-4 py-2">
                                      Unit Price
                                    </th>
                                  )}
                                  {columnVisibility.total && (
                                    <th className="border border-gray-300 px-2 md:px-4 py-2 text-end">
                                      Total
                                    </th>
                                  )}
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody className="text-center">
                            {quotationItems.map((item, index) =>
                              visibleColumns ? (
                                <tr key={item.id}>
                                  {visibleColumns.map((col) => {
                                    const isDesc = col.id === "description";
                                    const isEnd = col.type === "formula";
                                    let content: React.ReactNode;
                                    if (col.id === "sno") content = index + 1;
                                    else if (
                                      col.type === "formula" &&
                                      col.formula
                                    ) {
                                      const row = getRowForFormula(item, index);
                                      const val = evaluateFormula(
                                        col.formula,
                                        row,
                                        columns,
                                      );
                                      content = Number.isFinite(val)
                                        ? val.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                        : String(val ?? "");
                                    } else if (col.id === "description")
                                      content = item.description ?? "";
                                    else {
                                      const v = getCellValue(item, col.id);
                                      content =
                                        typeof v === "number"
                                          ? (v as number).toLocaleString(
                                              undefined,
                                              {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              },
                                            )
                                          : v;
                                    }
                                    return (
                                      <td
                                        key={col.id}
                                        className={
                                          isDesc
                                            ? "border-x border-gray-300 text-start px-2 md:px-4 py-2"
                                            : isEnd
                                              ? "border-x border-gray-300 px-2 md:px-4 py-2 text-end tabular-nums min-w-[7rem]"
                                              : "border-x border-gray-300 px-2 md:px-4 py-2"
                                        }
                                      >
                                        {content}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ) : (
                                <tr key={item.id}>
                                  <td className="border-x border-gray-300 px-2 md:px-4 py-2">
                                    {index + 1}
                                  </td>
                                  {columnVisibility.description && (
                                    <td className="border-x border-gray-300 text-start px-2 md:px-4 py-2">
                                      {item.description}
                                    </td>
                                  )}
                                  {columnVisibility.unit && (
                                    <td className="border-x border-gray-300 px-2 md:px-4 py-2">
                                      {item.unit}
                                    </td>
                                  )}
                                  {columnVisibility.quantity && (
                                    <td className="border-x border-gray-300 px-2 md:px-4 py-2">
                                      {item.quantity}
                                    </td>
                                  )}
                                  {columnVisibility.unitPrice && (
                                    <td className="border-x border-gray-300 px-2 md:px-4 py-2">
                                      {parseFloat(
                                        item.unitPrice,
                                      ).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                  )}
                                  {columnVisibility.total && (
                                    <td className="border-x border-gray-300 px-2 md:px-4 py-2 text-end">
                                      {(
                                        item.quantity *
                                        parseFloat(item.unitPrice)
                                      ).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                  )}
                                </tr>
                              ),
                            )}

                            {visibleColumns && formulaCols.length > 0 ? (
                              <>
                                {formulaCols.length > 1 && (
                                  <tr className="border-t border-gray-300">
                                    {visibleColumns.map((col, i) => {
                                      const isDesc = col.id === "description";
                                      const isFormula =
                                        col.type === "formula" && col.formula;
                                      const totalLabelCol = Math.max(
                                        0,
                                        visibleColumns.length -
                                          formulaCols.length -
                                          1,
                                      );
                                      let content: React.ReactNode = "";
                                      if (isFormula) {
                                        if (col.id === lineTotalFormulaId) {
                                          content = "";
                                        } else {
                                          const idx = formulaCols.findIndex(
                                            (fc) => fc.id === col.id,
                                          );
                                          const sum =
                                            idx >= 0 ? formulaSums[idx] : 0;
                                          content = sum.toLocaleString(
                                            undefined,
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          );
                                        }
                                      } else if (i === totalLabelCol) {
                                        content = "";
                                      }
                                      return (
                                        <td
                                          key={col.id}
                                          className={
                                            isDesc
                                              ? "border border-gray-300 text-start px-2 md:px-4 py-2 font-semibold"
                                              : isFormula
                                                ? "border border-gray-300 px-2 md:px-4 py-2 font-semibold text-end tabular-nums min-w-[7rem]"
                                                : "border border-gray-300 px-2 md:px-4 py-2 font-semibold"
                                          }
                                        >
                                          {content}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                )}
                                <tr
                                  className={
                                    formulaCols.length > 1
                                      ? undefined
                                      : "border-t border-gray-300"
                                  }
                                >
                                  <td
                                    colSpan={visibleColumns.length - 1}
                                    className="text-end px-2 md:px-4 py-2 font-semibold"
                                  >
                                    Sub-total
                                  </td>
                                  <td className="border border-gray-300 px-2 md:px-4 py-2 font-semibold text-end">
                                    {quotation.currencyType}{" "}
                                    {subtotal.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </td>
                                </tr>
                              </>
                            ) : (
                              <tr className="border-t border-gray-300">
                                <td
                                  colSpan={
                                    visibleColumns
                                      ? visibleColumns.length - 1
                                      : 1 +
                                        (columnVisibility.description ? 1 : 0) +
                                        (columnVisibility.unit ? 1 : 0) +
                                        (columnVisibility.quantity ? 1 : 0) +
                                        (columnVisibility.unitPrice ? 1 : 0)
                                  }
                                  className="text-end px-2 md:px-4 py-2 font-semibold"
                                >
                                  Sub-total
                                </td>
                                <td className="border border-gray-300 px-2 md:px-4 py-2 font-semibold text-end">
                                  {quotation.currencyType}{" "}
                                  {subtotal.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </tr>
                            )}

                            {quotation.showIsVatExempted ? (
                              <tr>
                                <td
                                  colSpan={
                                    visibleColumns
                                      ? visibleColumns.length - 1
                                      : 1 +
                                        (columnVisibility.description ? 1 : 0) +
                                        (columnVisibility.unit ? 1 : 0) +
                                        (columnVisibility.quantity ? 1 : 0) +
                                        (columnVisibility.unitPrice ? 1 : 0)
                                  }
                                  className="text-end px-2 md:px-4 py-2 font-semibold"
                                >
                                  VAT Exempt
                                </td>
                                <td className="border border-gray-300 px-2 md:px-4 py-2 font-semibold text-end">
                                  —
                                </td>
                              </tr>
                            ) : quotation.isVatIncluded ? (
                              <tr>
                                <td
                                  colSpan={
                                    visibleColumns
                                      ? visibleColumns.length - 1
                                      : 1 +
                                        (columnVisibility.description ? 1 : 0) +
                                        (columnVisibility.unit ? 1 : 0) +
                                        (columnVisibility.quantity ? 1 : 0) +
                                        (columnVisibility.unitPrice ? 1 : 0)
                                  }
                                  className="text-end px-2 md:px-4 py-2 font-semibold"
                                >
                                  VAT (18%)
                                </td>
                                <td className="border border-gray-300 px-2 md:px-4 py-2 font-semibold text-end">
                                  {quotation.currencyType}{" "}
                                  {vat.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              </tr>
                            ) : null}

                            <tr>
                              <td
                                colSpan={
                                  visibleColumns
                                    ? visibleColumns.length - 1
                                    : 1 +
                                      (columnVisibility.description ? 1 : 0) +
                                      (columnVisibility.unit ? 1 : 0) +
                                      (columnVisibility.quantity ? 1 : 0) +
                                      (columnVisibility.unitPrice ? 1 : 0)
                                }
                                className="text-end px-2 md:px-4 py-2 font-bold"
                              >
                                Total
                              </td>
                              <td className="border border-gray-300 px-2 md:px-4 py-2 font-bold text-end">
                                {quotation.currencyType}{" "}
                                {total.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {quotation.notes && (
                        <div className="mt-4">
                          <h2 className="text-sm md:text-md font-semibold mb-1">
                            Note
                          </h2>
                          <div className="whitespace-pre-wrap text-sm">
                            {quotation.notes}
                          </div>
                        </div>
                      )}

                      {quotation.bankDetail && (
                        <section className="mt-6 md:mt-10 text-xs md:text-sm">
                          <h2 className="text-sm md:text-md font-semibold mb-1">
                            BANK INFORMATION
                          </h2>
                          {quotation.bankDetail ? (
                            <p>
                              <strong>BANK NAME:</strong>{" "}
                              {quotation.bankDetail.bankName}
                              <br />
                              <strong>ACCOUNT NAME:</strong>{" "}
                              {quotation.bankDetail.accountName}
                              <br />
                              <strong>ACCOUNT NO:</strong>{" "}
                              {quotation.bankDetail.accountNumber}
                              <br />
                              <strong>SWIFT CODE:</strong>{" "}
                              {quotation.bankDetail.swiftCode}
                              <br />
                              <strong>BRANCH:</strong>{" "}
                              {quotation.bankDetail.branchName}
                            </p>
                          ) : (
                            <p className="text-gray-600">
                              No bank information provided.
                            </p>
                          )}
                        </section>
                      )}
                    </div>
                  </ScaledPreview>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">No quotation data</p>
                </div>
              )}
            </PanelScroll>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

const PanelScroll: React.FC<{
  isMobile: boolean;
  children: React.ReactNode;
}> = ({ isMobile, children }) =>
  isMobile ? (
    <div className="h-full overflow-y-auto overscroll-contain">{children}</div>
  ) : (
    <ScrollArea className="h-full">{children}</ScrollArea>
  );

export default QuoteDrawer;
